import { defineStore } from 'pinia';
import axios from 'axios';
import { useMainStore } from '@/stores/useMainStore';
import { useAppStore } from '@/stores/useAppStore';
import { randomOrder } from '@/utils/data/resources';
import { reportFatalError, reportNonFatalError } from '@/utils/storeErrorReporting';
import {
    getTokenNotFoundError,
    getFailedOrgFetchError,
    getOrgViewError,
    getOrgSaveError,
    getOrgActivateError,
    getOrgEmployeesCreateError,
    getOrgEmployeeUpdateError,
    getOrgEmployeeRemoveError,
    getOrgAvatarUpdateError,
    getOrganizationsListError,
    getOrgEmployeesListError,
    getOrgEmployeesExportError,
} from '@/utils/errorMessages';
import { fetchOrganizationVolunteers } from '@/api/employees';

const API_DOMAIN = import.meta.env.VITE_API_URL;

export const useOrganizationStore = defineStore('organization', {
    state: () => ({
        organization: {
            name: '',
            avatarUrl: '',
            organizationId: null,
        },
        employees: [],
        totalEmployeesCount: null,
        filteredEmployeesCount: null,
    }),

    getters: {
        /**
         * Returns the organization ID
         */
        organizationId: (state) => state.organization?.organizationId,
    },

    actions: {
        /**
         * Updates organization state by merging new data
         * @param {Object} data - Data to merge into organization state
         */
        updateOrganization(data) {
            this.organization = { ...this.organization, ...data };
        },

        /**
         * Empties the cached organization employees list
         */
        clearEmployees() {
            this.employees = [];
        },

        /**
         * Replaces organization state with new data
         * @param {Object} data - Data to replace organization state
         */
        setOrganization(data) {
            this.organization = data;
        },

        /**
         * Helper: Fetches organization data from API
         * Single source of truth for fetching organization data
         * @param {string} organizationId - Organization ID to fetch
         * @param {string} token - Authorization token
         * @returns {Promise<Object>} Organization data
         */
        async fetchOrganizationData(organizationId, token) {
            const response = await axios({
                method: 'GET',
                url: `${API_DOMAIN}/organizations/${organizationId}`,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        },

        /**
         * Fetches organization data from API (for current user's organization)
         * @param {Object} payload - Contains token and organizationId
         */
        async getOrganization(payload) {
            const mainStore = useMainStore();

            // Check if token exists before making the request
            if (!payload.token) {
                reportFatalError(new Error('Token not found'), getTokenNotFoundError());
                return;
            }

            try {
                mainStore.updateLoading({ key: 'organization', status: true });

                // Use shared helper for fetching organization data
                const data = await this.fetchOrganizationData(payload.organizationId, payload.token);

                this.setOrganization(data);
                mainStore.updateLoading({ key: 'organization', status: false });
            } catch (error) {
                mainStore.updateLoading({ key: 'organization', status: false });
                reportFatalError(error, getFailedOrgFetchError());
            }
        },

        /**
         * Saves organization data to API
         * @param {Object} payload - Contains token
         */
        async saveOrganization(payload) {
            const mainStore = useMainStore();

            try {
                mainStore.updateLoading({ key: 'organization_save', status: true });

                const response = await axios({
                    method: 'POST',
                    url: `${API_DOMAIN}/organizations/${this.organization.organizationId}`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                    data: this.organization,
                });

                this.setOrganization(response.data);
                mainStore.updateLoading({ key: 'organization_save', status: false });
            } catch (error) {
                mainStore.updateLoading({ key: 'organization_save', status: false });
                reportNonFatalError(error, getOrgSaveError());
            }
        },

        /**
         * Views organization and caches data in main store (for browsing any organization)
         * @param {Object} payload - Contains token and data with organizationId
         */
        async viewOrganization(payload) {
            const mainStore = useMainStore();

            try {
                const cachedOrgs = mainStore.page.organization;

                // If organization already cached, return early
                if (cachedOrgs && cachedOrgs[payload.data.organizationId]) {
                    return;
                }

                mainStore.updateLoading({ key: 'page', status: true });

                // Use shared helper for fetching organization data
                const data = await this.fetchOrganizationData(payload.data.organizationId, payload.token);

                // Merge with existing cached organizations
                const orgDataObj = {
                    ...(cachedOrgs || {}),
                    [payload.data.organizationId]: data,
                };

                mainStore.updatePage({ organization: orgDataObj });
                mainStore.updateLoading({ key: 'page', status: false });
            } catch (error) {
                mainStore.updateLoading({ key: 'page', status: false });
                reportNonFatalError(error, getOrgViewError());
            }
        },

        /**
         * Creates organization employees (invites)
         * @param {Object} payload - Contains token and data with employee information
         */
        async createOrganizationEmployees(payload) {
            try {
                const mainStore = useMainStore();
                mainStore.updateLoading({ key: 'invites', status: true });

                const response = await axios({
                    method: 'POST',
                    url: `${API_DOMAIN}/organizations/${this.organization.organizationId}/employees`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                    data: payload.data,
                });

                if (response.data.organizationId) {
                    this.updateOrganization(response.data);
                }

                mainStore.updateLoading({ key: 'invites', status: false });
            } catch (error) {
                const mainStore = useMainStore();
                mainStore.updateLoading({ key: 'invites', status: false });
                reportNonFatalError(error, getOrgEmployeesCreateError());
            }
        },

        /**
         * Updates an organization employee
         * @param {Object} payload - Contains token and data with employeeId and update data
         */
        async updateOrganizationEmployee(payload) {
            try {
                const mainStore = useMainStore();
                const { employeeId, ...updateData } = payload.data;

                if (!employeeId) {
                    console.error('No employeeId provided');
                    return;
                }

                mainStore.updateLoading({ key: 'list_employees', status: true });

                const response = await axios({
                    method: 'POST',
                    url: `${API_DOMAIN}/organizations/${this.organization.organizationId}/employees/${employeeId}`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                    data: updateData,
                });

                if (response.data[0]) {
                    this.employees = response.data;
                }

                mainStore.updateLoading({ key: 'list_employees', status: false });
            } catch (error) {
                const mainStore = useMainStore();
                mainStore.updateLoading({ key: 'list_employees', status: false });
                reportNonFatalError(error, getOrgEmployeeUpdateError());
            }
        },

        /**
         * Removes an employee from the organization
         * @param {Object} payload - Contains token and data with employeeId
         */
        async removeEmployee(payload) {
            const mainStore = useMainStore();

            try {
                mainStore.updateLoading({ key: 'organization_employee_remove', status: true });

                const response = await axios({
                    method: 'DELETE',
                    url: `${API_DOMAIN}/organizations/${this.organization.organizationId}/employees/${payload.data.employeeId}`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                this.updateOrganization(response.data);
                mainStore.updateLoading({ key: 'organization_employee_remove', status: false });
            } catch (error) {
                mainStore.updateLoading({ key: 'organization_employee_remove', status: false });
                reportNonFatalError(error, getOrgEmployeeRemoveError());
            }
        },

        /**
         * Updates organization avatar
         * @param {Object} payload - Contains token and data with fileContent
         */
        async updateOrgAvatar(payload) {
            const mainStore = useMainStore();

            try {
                mainStore.updateLoading({ key: 'organization_avatar', status: true });

                const response = await axios({
                    method: 'POST',
                    url: `${API_DOMAIN}/organizations/${this.organization.organizationId}/update-avatar`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                        'Content-Type': 'application/json',
                    },
                    data: payload.data.fileContent,
                });

                this.updateOrganization(response.data);
                mainStore.updateLoading({ key: 'organization_avatar', status: false });
            } catch (error) {
                mainStore.updateLoading({ key: 'organization_avatar', status: false });
                reportNonFatalError(error, getOrgAvatarUpdateError());
            }
        },

        /**
         * Fetches organizations list with optional filtering
         * @param {Object} payload - Contains token and optional data with max and params
         */
        async getOrganizations(payload) {
            const mainStore = useMainStore();
            const appStore = useAppStore();

            try {
                mainStore.updateLoading({ key: 'organizations_list', status: true });

                let params = '';
                if (payload.data && payload.data.params) {
                    params = `?${new URLSearchParams(payload.data.params).toString()}`;
                }

                const response = await axios({
                    method: 'GET',
                    url: `${API_DOMAIN}/organizations${params}`,
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                mainStore.updatePagination(response.data);

                let organizations = response.data.records || [];

                // If no organizations with organizationId, return empty
                if (!organizations.some((org) => org.organizationId)) {
                    mainStore.updatePage({ organizations: [] });
                    mainStore.updateLoading({ key: 'organizations_list', status: false });
                    return;
                }

                // Handle spotlight highlighting and randomization
                const { spotlight } = appStore;
                if (spotlight && spotlight.id) {
                    const highlightedIndex = organizations.findIndex((org) => org.organizationId === spotlight.id);
                    if (highlightedIndex !== -1) {
                        const [highlighted] = organizations.splice(highlightedIndex, 1);
                        organizations = randomOrder(organizations);
                        organizations.unshift(highlighted);
                    }
                } else {
                    organizations = randomOrder(organizations);
                }

                // Limit to max if specified
                if (payload.data && payload.data.max && organizations.length > payload.data.max) {
                    organizations = organizations.slice(0, payload.data.max);
                }

                mainStore.updatePage({ organizations });
                mainStore.updateLoading({ key: 'organizations_list', status: false });
            } catch (error) {
                mainStore.updateLoading({ key: 'organizations_list', status: false });
                reportNonFatalError(error, getOrganizationsListError());
            }
        },

        /**
         * Fetches organization employees
         * @param {Object} payload - Contains token and optional data with organizationId for page view
         */
        async getOrganizationEmployees(payload) {
            // TODO: 2026-08-14: Volunteers page for a specific organization is handled by atlas apps
            // so plan to drop dead code from all stores that touch that feature.
            const mainStore = useMainStore();

            const orgAdminsOrganization = this.organization?.organizationId;
            const selectedOrganizationId = payload?.data?.organizationId;

            const targetOrganizationId = selectedOrganizationId ?? orgAdminsOrganization;

            // Organization - Logged in as organization admin and is searching volunteers
            //  in their own org.
            // Page         - Any other user that is searching for volunteers in
            //  a specificied organization.
            const contextOfStoreUsage = selectedOrganizationId ? 'page' : 'organization';
            try {
                mainStore.updateLoading({
                    key: contextOfStoreUsage,
                    status: true,
                });

                const response = await fetchOrganizationVolunteers(targetOrganizationId);
                if (!response) return;

                let toStore = response.records || [];

                // Add avatarUrl property if needed
                if (toStore.length > 0 && toStore[0].avatar_url) {
                    toStore = toStore.map((employee) => ({
                        ...employee,
                        avatarUrl: employee.avatar_url,
                    }));
                }

                if (contextOfStoreUsage === 'page') {
                    mainStore.updatePage({ employees: toStore });
                } else {
                    this.employees = toStore;
                }
            } catch (error) {
                reportNonFatalError(error, getOrgEmployeesListError());
            } finally {
                mainStore.updateLoading({
                    key: contextOfStoreUsage,
                    status: false,
                });
            }
        },

        /**
         * Exports organization employees to CSV file
         * Downloads a CSV file containing all employees of the organization.
         * @param {string} organizationId - The organization ID to export employees from
         * @param {Object} payload - Contains token for authorization
         */
        async exportEmployeesToCSV(organizationId, payload) {
            const mainStore = useMainStore();

            try {
                mainStore.updateLoading({ key: 'organization_employees_export', status: true });

                const response = await axios.get(`${API_DOMAIN}/organizations/${organizationId}/employees/csv`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                const blob = new Blob([atob(response.data)], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'organization_employees.csv');
                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                mainStore.updateLoading({ key: 'organization_employees_export', status: false });
            } catch (error) {
                mainStore.updateLoading({ key: 'organization_employees_export', status: false });
                reportNonFatalError(error, getOrgEmployeesExportError());
            }
        },
    },
});
